import React, { useEffect, useState } from 'react';
import { useContent } from '../content/ContentContext';
import { HistoryEntry } from '../content/store';

// Change history / audit log. Lists recent edits (who changed what, when) across
// all fields, and lets an editor restore any prior value — which stages the old
// value so it goes live on the next Save.
export function HistoryPanel({ onClose }: { onClose: () => void }) {
  const { recentAudit, storeKind, setField } = useContent();
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');

  useEffect(() => {
    let live = true;
    recentAudit()
      .then(e => { if (live) setEntries(e); })
      .catch(() => { if (live) setError('Could not load history.'); });
    return () => { live = false; };
  }, [recentAudit]);

  function restore(en: HistoryEntry) {
    setField(en.path, en.value);
    setFlash(`Staged restore of ${en.path} — press Save to publish.`);
    setTimeout(() => setFlash(''), 3000);
  }

  const preview = (v: unknown) => {
    const s = typeof v === 'string' ? v : JSON.stringify(v);
    return s.length > 90 ? s.slice(0, 90) + '…' : s;
  };
  const when = (iso: string) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="signin__overlay" onClick={onClose}>
      <div className="history" role="dialog" aria-modal="true" aria-label="Change history" onClick={e => e.stopPropagation()}>
        <button className="signin__close" onClick={onClose} aria-label="Close">×</button>
        <h3>Change history</h3>

        {storeKind === 'local' && (
          <p className="signin__note">Local preview keeps no server history — this appears once edits save to the live site.</p>
        )}
        {error && <p className="signin__error">{error}</p>}
        {flash && <p className="signin__hint">{flash}</p>}

        {!entries && !error && <p className="history__empty">Loading…</p>}
        {entries && entries.length === 0 && <p className="history__empty">No changes recorded yet.</p>}

        {entries && entries.length > 0 && (
          <ul className="history__list">
            {entries.map((en, i) => (
              <li key={i} className="history__item">
                <div className="history__meta">
                  <code className="history__path">{en.path}</code>
                  <span className="history__who">{en.editor}</span>
                  <span className="history__when">{when(en.at)}</span>
                </div>
                <div className="history__val">{preview(en.value)}</div>
                <button className="editbar__btn history__restore" onClick={() => restore(en)}>
                  Restore this value
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
